<script lang='ts'>
    import { fade } from "svelte/transition";
    import route from "@/Util/routes";
    import Modal from "../../Bootstrap/Modal.svelte";
    import ProgressionChart from "../TrainingForecast/ProgressionChart.svelte";
    import type { MedianAndWeatherStores, MyFitnessStores } from "../stores";
    import { waitForForecastWorkouts, fetchTrainingAdvisor, adaptForecast, getForecastResultsForChart, getRequest, mergeAdaptedForecast, throttleTimeout, uniqueId } from "@/Util/utils";
    import { date, _ } from 'svelte-i18n'
    import { getContext, onMount } from "svelte";
    import ProgressBar from "../TrainingForecast/ProgressBar.svelte";
    import { killWorker } from "@/xert-wasm";
    import Am5Loader from "../../Util/Am5Loader.svelte";
    import { addMonths, fromUnixTime, startOfDay, startOfWeek } from 'date-fns';
    import LoadingAnimation from "./LoadingAnimation.svelte";
    import { Activity } from '@/Common/ActivityFunctions';
    import * as Dialog from "$lib/components/ui/dialog";
    import { Button } from "$lib/components/ui/button/index.js";
    import * as Tooltip from "$lib/components/ui/tooltip";
    import * as Tabs from "$lib/components/ui/tabs";
    import { tick } from "svelte";
    import AdaptForecastDetails from "./AdaptForecastDetails.svelte";
    import AdaptForecastSummary from "./AdaptForecastSummary.svelte";
    import { changedDayKeys } from "./AdaptForecastDayCard.svelte";
    import { cn } from "@/lib/utils";
    import Separator from "@/lib/components/ui/separator/separator.svelte";
    import { Checkbox } from "@/lib/components/ui/checkbox";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { CyclingProgress } from "@/Common";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";
    import { SCHEDULE_WORKOUTS_HELP } from "@/Common/constants";

    const id = uniqueId('calAdaptModal-')
    export let forecastData: any[] | null
    export let today
    export let onConfirm = null
    
    //@ts-ignore
    const jq = window.$;

    let modalElement: any;
    let modalState = 'closed'
    const { trainingPlan, userParams, cal_settings } = getContext<MyFitnessStores>("myFitness")
    const { trainingMedians } = getContext<MedianAndWeatherStores>("medianAndWeather")

    let doRunTrainingForecast = false;
    let forecastProgress, forecastMessage;
    let forecastWarning = false, forecastError: string | null = null, forecastSuccess: string | null = null;
    let forecastHelp: string | null = null, forecastDetail: string | null = null, forecastReasons: string[] = [], forecastTips: string[] = [], forecastShortfall: string[] = [], forecastOverage: string | null = null, forecastWarningTitle: string | null = null, forecastTipsIntro: string | null = null;
    let chartData = [];
    let chartRange = null;
    let open = false
    //@ts-ignore
    const Xert = window.Xert;

    onMount(()=>{
        if (forecastData) {
            chartData = forecastData
        }
    })

    function openChange(open: boolean) {
        if(open) {
            // modalState = 'loading'
            // loadUnpinnedActivities();
        } else {
            modalState = 'closed'
        }
    }
    
    export function openModal() {
        // open = true
        // openChange(open)
        loadUnpinnedActivities();
    }

    export function closeModal() {
        open = false
        openChange(open)
    }

    let unpinnedActivities;
    // The most rows the confirmation lists without scrolling. A longer list starts collapsed under a heading, and expands into a
    // scroll area this many rows tall, so the modal keeps the height it has with this many rows.
    const UNPINNED_ROWS_VISIBLE = 5;
    const UNPINNED_ROW_HEIGHT = 48; // px, a row plus its margin
    let unpinnedListShown = false;
    $: unpinnedCollapsible = (unpinnedActivities?.length ?? 0) > UNPINNED_ROWS_VISIBLE;
    $: unpinnedKept = unpinnedActivities?.filter(a => Activity.isPinned(a)).length ?? 0;
    async function loadUnpinnedActivities() {
        unpinnedActivities = (await window.fetch('/calendar/events?'+new URLSearchParams({
            forUser: this.username,
            start: today.toISOString(),
            end: addMonths(today, 3).toISOString(),
            unpinned: 'true',
        }), {
            method: 'GET',
            ...getRequest()
        }).then(r=>r.json()));

        if (unpinnedActivities != null && unpinnedActivities.length > 0) {
            modalState = 'confirmation'
            open = true
        } else {
            _onConfirm()
        }
    }

    function _onConfirm() {
        if (onConfirm != null) {
            closeModal()
            onConfirm()
        } else {
            modalState = 'forecast'
            open = true
            recalculateForecast()
        }
    }

    function pinActivity(e, activity) {
        e.target.blur();
        jq('[data-toggle="tooltip"]').tooltip('hide');
        Activity.togglePin(activity);
        // this is so svelte knows it needs to re-render the list
        unpinnedActivities = unpinnedActivities
    }

    let forecastResultsForOutput = null;
    // Current plan vs. adapted forecast days, for the Details tab
    let detailsBefore: any[] = [], detailsAfter: any[] = [], detailsReady = false;
    let activeTab = 'chart';
    // The Summary tab holds the outcome (success, warning or error); it has something to show once a run has finished.
    // The days the adapted forecast changes, drawn in darker colours on the chart.
    $: changedDates = detailsReady ? changedDayKeys(detailsBefore, detailsAfter) : []
    $: hasSummary = !!(forecastError || forecastWarning || (forecastSuccess && detailsReady))
    let adaptedFlash: 'success' | 'warning' | 'error' | null = null, adaptedFlashTimer: ReturnType<typeof setTimeout> | null = null;
    function showAdaptedThenSummary(kind: 'success' | 'warning' | 'error') {
        if (activeTab !== 'chart') { activeTab = 'summary'; return }
        adaptedFlash = kind
        adaptedFlashTimer = setTimeout(() => { adaptedFlash = null; adaptedFlashTimer = null; activeTab = 'summary' }, 2500)
    }
    function cancelAdaptedFlash() {
        if (adaptedFlashTimer) clearTimeout(adaptedFlashTimer)
        adaptedFlashTimer = null
        adaptedFlash = null
    }
    let tabsEl: HTMLElement;
    // On small screens the warning pushes the list down: bring the tabs to the top when switching so the list gets the space.
    $: if (activeTab === 'details') tick().then(() => tabsEl?.scrollIntoView({ block: 'start', behavior: 'smooth' }))


    // Assign workouts to the forecasted days when saving. On by default when the plan's target is within 3 months,
    // until the user changes it.
    let scheduleWorkouts = true
    let savingForecast = false
    let scheduleWorkoutsTouched = false
    $: targetDay = forecastResultsForOutput?.days?.find(d => d.state?.includes('event_A')) ?? forecastResultsForOutput?.days?.at(-1)
    $: if (!scheduleWorkoutsTouched && targetDay) scheduleWorkouts = startOfDay(fromUnixTime(targetDay.t)) <= startOfDay(addMonths(new Date(), 3))
    function scheduleForecast() {
        if (forecastResultsForOutput == null) return Promise.reject();
        const result = forecastResultsForOutput;

        // TODO: no need to pass dates?
        // Before a plan starts (Pre-Plan Maintenance) it is adapted as of its start date, not today. Adapt can't
        // move the start date, so result's own first day (the forecast about to be saved) and the plan's current
        // forecastResults should agree; prefer result's since it's the freshest. If pre-plan and neither source has
        // it, stop rather than silently scheduling from today, which would wrongly move the plan's start to now.
        // Checked before clearing the result, and returning rather than rejecting (this is a click handler, nothing
        // would catch it), so Save stays usable for a retry once the store has settled.
        const isPrePlan = $trainingPlan?.is_pre_plan
        const prePlanStart = result?.days?.[0]?.t ?? $trainingPlan?.forecastResults?.days?.[0]?.t
        if (isPrePlan && !prePlanStart) {
            forecastError = 'Could not determine the plan\'s start date. Please try again.'
            return
        }
        forecastResultsForOutput = null;
        savingForecast = true
        // The dialog sits above the progress overlay (its portal's z-index), so close it now: the result is already taken.
        closeModal()
        const fromDate = isPrePlan ? fromUnixTime(prePlanStart) : new Date()
        const toDate = new Date()
        const durationDays = -1;
        toDate.setDate(toDate.getDate() + durationDays);

        // Workouts a saved plan couldn't get straight away are generated in the background
        const workoutsComplete = waitForForecastWorkouts()

        // NOTE: Same endpoint as updating training program, does it work OK without a lot of the parameters?
        return window.fetch(route('UserController@postTrainingProgram'), {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify({
            fromDate: fromDate.toISOString(),
            toDate: toDate.toISOString(),
            duration: durationDays,
            program_type: 'targetEvent', // This is called after adaptForecast, it must be a targetEvent
            result,
            schedule_workouts: scheduleWorkouts,
            save_id: workoutsComplete.saveId,
        })
        }).then(r=>r.json()).then(async data=>{
            if (data.workouts_pending) {
                await workoutsComplete.promise
                // The response is from before the workouts were added: read the plan again
                const fresh = await fetchTrainingAdvisor().catch(() => null)
                if (fresh?.training_plan) data = { ...data, training_plan: fresh.training_plan }
            } else workoutsComplete.cancel()
            if (data.result != false && data.training_plan) {
                trainingPlan.set(data.training_plan);
            }
        }).catch(() => {
            workoutsComplete.cancel()
            Xert.warning("Failed to save the adapted forecast. Please try again.")
        }).finally(() => savingForecast = false)
    }

    function resetForecastChart() {
        forecastMessage = ""
        forecastProgress = null
        forecastSuccess = null
        forecastWarning = false
        forecastError = null
        forecastHelp = null
        forecastDetail = null
        forecastReasons = []
        forecastTips = []
        forecastShortfall = []
        forecastOverage = null
        chartRange = null
        forecastResultsForOutput = null
        detailsReady = false
        cancelAdaptedFlash()
        activeTab = 'chart'
        trainingPlan.set($trainingPlan)
    }

    // Update forecast to get back on track.
    async function recalculateForecast() {
        if ($trainingPlan && $trainingPlan.forecastResults) {

            const results = $trainingPlan.forecastResults;
            const days = $trainingPlan.forecastResults.days as any[]
            const n = days.length

            if (n > 0) {
                const result = await getForecastResultsForChart(results, $trainingPlan.settings, $cal_settings, $trainingMedians)
                const forecastResults = result.forecastResults;
                chartData = forecastResults['days'];
            }


            forecastError = null;
            forecastHelp = null;
            forecastDetail = null;
            forecastReasons = [];
            forecastTips = [];
            forecastShortfall = [];
            forecastOverage = null;
            forecastWarning = false;
            forecastSuccess = null;
            forecastResultsForOutput = null;
            detailsReady = false;
            cancelAdaptedFlash()
            activeTab = 'chart';

            const toDate = new Date(days[n-1].t*1000)
            // To get back on track: get inputs just from now to the end. Then, merge the new results to the old data that's before now.
            // Before a plan starts (Pre-Plan Maintenance) it is adapted as of its start date, not today
            const prePlanFromDate = $trainingPlan.is_pre_plan ? fromUnixTime(days[0].t).toISOString() : null
            const trainingForecastInput = await window.fetch(route('CalendarController@getTrainingForecast') + '?' + new URLSearchParams({
                ...(prePlanFromDate ? { fromDate: prePlanFromDate } : {}),
                toDate: toDate.toISOString(),
                duration: '-1',
                includePlaceholders: 'true', // Set flag to return previously scheduled placeholder activities, by default the forecast inputs skip those for generating a new forecast.
            }), {
            ...getRequest(),
            }).then(r=>r.json())

            // Keep chunk of past data to prepend to updated data.
            // const prependData = days.slice(0, n - newDays.length);
            const prependData = chartData.slice(0, chartData.length - trainingForecastInput.days.length);
            const i0 = prependData.length

            // const forecastResults = (await getLibxert()).get_back_on_track({...results.inputs, ...trainingForecastInput, original_days: trainingPlan.forecastResults.days})
            // NOTE: results.inputs used to get extra params like lowRestDays etc. from previous run.
            // const forecastResults = await (await getLibxert())
            doRunTrainingForecast = true;
            forecastProgress = 0.01;
            forecastMessage = 'Working...';

            let pendingData = [];
            const chartThrottle = throttleTimeout(()=>{
                chartData = pendingData;
            }, 1000)
            const { result, inputs } = await adaptForecast($trainingPlan.forecastResults, trainingForecastInput, $trainingPlan.settings, $cal_settings.defaults, $cal_settings.availabilities, $trainingMedians, (message)=>{
                if (message['days']) {
                    pendingData = [...prependData, ...message['days']]
                    chartThrottle.trigger()
                }

                if (message['debug']) {
                    const debug = message['debug']
                    forecastProgress = debug['progress']
                    forecastMessage = debug['msg']
                    chartRange = [i0+debug['i0'], i0+debug['i1']]
                }
            })
            chartThrottle.clear()

            const forecastResults = result
            
            doRunTrainingForecast = false

            if (forecastResults['success']) {
                chartData = [...prependData, ...forecastResults['days']]
                chartRange = null
                forecastProgress = null
                forecastMessage = ""
                // Keep the current plan's days next to the adapted ones so the changes can be inspected before saving.
                const adaptedDays = [...forecastResults['days']]
                const t0 = Math.round(startOfDay(fromUnixTime(adaptedDays[0].t)).getTime() / 1000)
                detailsBefore = ($trainingPlan.forecastResults?.days ?? []).filter(day => day.t >= t0)
                detailsAfter = adaptedDays
                detailsReady = true
                mergeAdaptedForecast($trainingPlan, inputs, forecastResults)
                forecastResultsForOutput = forecastResults

                // A forecast can be generated without reaching the target, or while going over Max Weekly Hours.
                // That isn't a failure: warn, but let the user save it.
                const overDays = forecastResults['days'].filter(day => (day.notes ?? '').includes('weekly_hours_exceeded'))
                if (overDays.length > 0) {
                    const weeks = new Set(overDays.map(day => startOfWeek(fromUnixTime(day.t), { weekStartsOn: 1 }).getTime())).size
                    const peak = Math.max(...overDays.map(day => day.weekly_hours ?? 0))
                    const maxHours = $trainingPlan?.settings?.maxWeeklyHours
                    forecastOverage = `${weeks} ${weeks == 1 ? 'week goes' : 'weeks go'} over your Max Weekly Hours${maxHours ? ` of ${maxHours}h` : ''}, up to ${peak.toFixed(1)}h in a week`
                }
                const targetDay = forecastResults['days'].find(day => /event_[lhp]tl_target/.test(day.notes ?? ''))
                // A Focus Goal's target is its focus power. The training load targets the optimizer works out from it can be missed
                // slightly while the goal itself is met, the same as the Forecast AI results (within 1 W counts as met).
                const focusPowerMet = !!targetDay && $trainingPlan?.selected_mode === 'target_focus' && $trainingPlan?.goal_info?.targets_mode !== 'manual'
                    && targetDay.target_fp > 0 && targetDay.fp > 0 && Math.round(targetDay.fp) - Math.round(targetDay.target_fp) >= -1
                if (targetDay && !focusPowerMet) {
                    const percent = (value: number, target: number) => Math.round(100 * value / target)
                    const mode = $trainingPlan?.selected_mode
                    if ((mode === 'target_focus' || mode === 'target_race') && targetDay.target_ftp > 0) {
                        // Goals and races: compare the signature (and focus power) the plan reaches with what the targets call for.
                        const rows = [
                            { label: 'Threshold Power', value: targetDay.ftp, target: targetDay.target_ftp, unit: 'W' },
                            { label: 'High Intensity Energy', value: targetDay.hie / 1000, target: targetDay.target_hie / 1000, unit: 'kJ', decimals: 1 },
                            { label: 'Peak Power', value: targetDay.pp, target: targetDay.target_pp, unit: 'W' },
                        ]
                        // Only when the plan was set up with a focus power target (not manual signature targets)
                        if (mode === 'target_focus' && $trainingPlan?.goal_info?.targets_mode !== 'manual' && targetDay.target_fp > 0 && targetDay.fp > 0) {
                            rows.push({ label: `${Math.round(targetDay.sfd / 60)} Minute Focus Power`, value: targetDay.fp, target: targetDay.target_fp, unit: 'W' })
                        }
                        forecastShortfall = rows
                            .filter(r => r.target > 0)
                            .map(r => `${r.label}: ${r.value.toFixed(r.decimals ?? 0)} ${r.unit} of ${r.target.toFixed(r.decimals ?? 0)} ${r.unit} (${percent(r.value, r.target)}%)`)
                    } else {
                        const loads = [
                            { note: 'event_ltl_target', label: 'Low', load: targetDay.tls?.l, target: targetDay.ltl_target },
                            { note: 'event_htl_target', label: 'High', load: targetDay.tls?.h, target: targetDay.htl_target },
                            { note: 'event_ptl_target', label: 'Peak', load: targetDay.tls?.p, target: targetDay.ptl_target },
                        ]
                        forecastShortfall = loads
                            .filter(l => targetDay.notes.includes(l.note) && l.target > 0)
                            .map(l => `${l.label} Training Load: ${l.load.toFixed(0)} of ${l.target.toFixed(0)} (${percent(l.load, l.target)}%)`)
                    }
                    forecastWarning = true
                    forecastWarningTitle = $_('forecast.adapt_warning_title')
                    forecastTipsIntro = $_('forecast.adapt_warning_tips_intro')
                    forecastHelp = $_('forecast.adapt_warning_help')
                    forecastTips = [$_('forecast.adapt_warning_tip_availability'), $_('forecast.adapt_warning_tip_hours'), $_('forecast.adapt_failed_tip_target')]
                } else if (forecastOverage) {
                    forecastWarning = true
                    forecastWarningTitle = $_('forecast.adapt_warning_hours_title')
                    forecastTipsIntro = $_('forecast.adapt_warning_hours_tips_intro')
                    forecastHelp = $_('forecast.adapt_warning_hours_help')
                    forecastTips = [$_('forecast.adapt_warning_tip_hours'), $_('forecast.adapt_failed_tip_target')]
                } else {
                    forecastSuccess = "Training Forecast Adapted Successfully"
                }
                // Say so on the chart for a moment, then show the outcome on the Summary tab.
                showAdaptedThenSummary(forecastWarning ? 'warning' : 'success')
            } else {
                resetForecastChart()
                showAdaptedThenSummary('error')

                forecastMessage = "";
                forecastError = $_('forecast.adapt_failed_title')
                if (forecastResults['error'] == 'taper') {
                    forecastHelp = $_('forecast.adapt_failed_taper')
                    forecastTips = [$_('forecast.adapt_failed_tip_taper_availability'), $_('forecast.adapt_failed_tip_taper_length')]
                } else {
                    forecastHelp = $_('forecast.adapt_failed_help_now')
                    forecastTips = [$_('forecast.adapt_failed_tip_availability_now'), $_('forecast.adapt_failed_tip_intensity_now'), $_('forecast.adapt_failed_tip_target')]
                    // i1 is the exclusive end of the optimized range, so the day that broke a constraint is the one before it.
                    const failedDay = forecastResults['days']?.[forecastResults["i1"] - 1]
                    const failedDate = failedDay ? new Date(failedDay.t*1000) : null
                    // Failing today means the whole plan fails, so only point at a date when it is in the future.
                    if (failedDay && startOfDay(failedDate) > startOfDay(new Date())) {
                        forecastHelp = $_('forecast.adapt_failed_help')
                        forecastTips = [$_('forecast.adapt_failed_tip_availability'), $_('forecast.adapt_failed_tip_intensity'), $_('forecast.adapt_failed_tip_target')]
                        forecastDetail = $date(failedDate, {dateStyle: "long"})
                    }
                    if (failedDay) {
                        // Explain what got in the way, using the same texts as the constraint notes elsewhere.
                        forecastReasons = [...new Set<string>((failedDay.notes ?? '').split(', ').filter(Boolean))]
                            .map(note => $_('forecast.constraints.' + note, { default: '' }))
                            .filter(Boolean)
                    }
                }

            }

        }

    }

    // Stop adapt if modal closed
    let shown = true
    $: if (!shown) killWorker()
</script>

{#if savingForecast}
    <CyclingProgress overlay listen={['progress']} title="Updating your training plan"
        messages={["Re-planning your training days…", "Choosing workouts…", "Pacing your training load…", "Updating your calendar…"]}/>
{/if}

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target" onOpenChange={openChange}>
    <Dialog.Content class={cn('text-black flex flex-col max-h-[100%] min-w-0', modalState == 'forecast' ? 'max-w-[800px]' : 'max-w-[600px]')}>
        <Dialog.Header>
            <Dialog.Title>Adapt Forecast</Dialog.Title>
        </Dialog.Header>
        {#if modalState == 'loading'}
            <div class="p-[20px] flex flex-col align-center">
                <LoadingAnimation/>
            </div>
        {:else if modalState == 'confirmation'}
            <div class='flex flex-row items-center'>
                <i class="fa-thin fa-triangle-exclamation fa-4x p-[10px]"/>
                <div class='flex-1 flex flex-col pl-[24px]'>
                    <div>
                        You have unpinned workouts and activities that will get modified when the adapted forecast is saved.
                    </div>
                    <div>
                        Select the activities you want to keep and pin on your calendar.
                    </div>
                </div>
            </div>
            <Separator/>
            {#if unpinnedCollapsible}
                <button class='bg-transparent border-none w-full flex flex-row items-center gap-[10px] px-[24px] py-[8px] text-start font-semibold'
                        aria-expanded={unpinnedListShown} on:click={() => unpinnedListShown = !unpinnedListShown}>
                    <i class={cn('fa-solid w-[16px]', unpinnedListShown ? 'fa-chevron-down' : 'fa-chevron-right')}/>
                    <span class='grow'>
                        {unpinnedActivities.length} unpinned {unpinnedActivities.length === 1 ? 'activity' : 'activities'}
                        {#if unpinnedKept > 0}<span class='font-normal text-muted-foreground'>({unpinnedKept} pinned)</span>{/if}
                    </span>
                    <span class='font-normal text-muted-foreground'>{unpinnedListShown ? 'Hide' : 'Show'}</span>
                </button>
            {/if}
            {#if !unpinnedCollapsible || unpinnedListShown}
            <div class='min-w-0 w-full flex flex-col overflow-y-auto' style={unpinnedCollapsible ? `max-height: ${UNPINNED_ROWS_VISIBLE * UNPINNED_ROW_HEIGHT}px` : ''}
                 class:grow={!unpinnedCollapsible}>
                {#each unpinnedActivities as activity}
                    <button class='bg-transparent hover:bg-gray-100 border-none w-full text-start flex flex-row align-center my-[8px] px-[24px] py-[4px] gap-[10px]'
                          on:click={(e) => pinActivity(e, activity)}>
                      <button> 
                          <i class={cn('fa-thumbtack fa-lg', Activity.isPinned(activity) ? 'fa-solid' : 'fa-light')}/>
                      </button>
                      <div class='min-w-[120px] text-muted-foreground'>
                          {(new Date(activity.start_date)).toLocaleDateString('en-US', {weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'})}
                      </div>
                      <div class='grow text-ellipsis whitespace-nowrap overflow-hidden'>
                          {activity.name}
                      </div>
                      <div class={cn('min-w-[70px]', !Activity.isPinned(activity) && 'text-destructive')}>
                          {Activity.isPinned(activity) ? 'PINNED' : 'UNPINNED'}
                      </div>
                  </button>
                {/each}
            </div>
            {/if}
        {:else if modalState == 'forecast'}
            <div class="p-[20px] overflow-y-auto">
                <ProgressBar progress={forecastProgress} message={forecastMessage} ></ProgressBar>
                <Tabs.Root bind:value={activeTab} class="mt-2" bind:el={tabsEl}>
                    <Tabs.List>
                        <Tabs.Trigger value="summary" disabled={!hasSummary}>Summary</Tabs.Trigger>
                        <Tabs.Trigger value="chart">Chart</Tabs.Trigger>
                        <Tabs.Trigger value="details" disabled={!detailsReady}>Details</Tabs.Trigger>
                    </Tabs.List>
                </Tabs.Root>
                <!-- Both panels stay mounted so switching tabs doesn't rebuild the chart. -->
                <div class="mt-2" hidden={activeTab !== 'summary'}>
                    {#if forecastError}
                        <div class="rounded-md border border-red-200 border-l-4 border-l-red-500 bg-red-50 p-4 mb-3 text-sm text-slate-800">
                            <div class="flex items-center gap-2 text-base font-bold text-red-700">
                                <i class="fa-solid fa-circle-xmark"></i>{forecastError}
                                {#if forecastDetail}<span class="ml-auto text-xs font-normal text-slate-500">{forecastDetail}</span>{/if}
                            </div>
                            {#if forecastHelp}<p class="mt-2">{forecastHelp}</p>{/if}
                            {#if forecastReasons.length > 0}
                                <div class="mt-3 font-semibold">{$_('forecast.adapt_failed_reasons')}</div>
                                <ul class="mt-1 list-disc pl-5 space-y-0.5">
                                    {#each forecastReasons as reason}<li>{reason}</li>{/each}
                                </ul>
                            {/if}
                            {#if forecastTips.length > 0}
                                <div class="mt-3 font-semibold">{$_('forecast.adapt_failed_tips_intro')}</div>
                                <ul class="mt-1 list-disc pl-5 space-y-0.5">
                                    {#each forecastTips as tip}<li>{tip}</li>{/each}
                                </ul>
                            {/if}
                        </div>
                    {:else if forecastWarning}
                        <div class="rounded-md border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50 p-4 mb-3 text-sm text-slate-800">
                            <div class="flex items-center gap-2 text-base font-bold text-amber-800">
                                <i class="fa-solid fa-triangle-exclamation"></i>{forecastWarningTitle}
                            </div>
                            {#if forecastHelp}<p class="mt-2">{forecastHelp}</p>{/if}
                            {#if forecastShortfall.length > 0}
                                <div class="mt-3 font-semibold">{$_('forecast.adapt_warning_shortfall')}</div>
                                <ul class="mt-1 list-disc pl-5 space-y-0.5">
                                    {#each forecastShortfall as item}<li>{item}</li>{/each}
                                </ul>
                            {/if}
                            {#if forecastOverage}
                                <div class="mt-3 font-semibold">{$_('forecast.adapt_warning_hours_label')}</div>
                                <ul class="mt-1 list-disc pl-5 space-y-0.5"><li>{forecastOverage}</li></ul>
                            {/if}
                            {#if forecastTips.length > 0}
                                <div class="mt-3 font-semibold">{forecastTipsIntro}</div>
                                <ul class="mt-1 list-disc pl-5 space-y-0.5">
                                    {#each forecastTips as tip}<li>{tip}</li>{/each}
                                </ul>
                            {/if}
                            <p class="mt-3 text-slate-600">{$_('forecast.adapt_warning_save')}</p>
                        </div>
                    {:else if forecastSuccess && detailsReady}
                        <AdaptForecastSummary before={detailsBefore} after={detailsAfter} plan={$trainingPlan} title={forecastSuccess} />
                    {/if}
                </div>
                <div class="mt-2 relative" hidden={activeTab !== 'chart'}>
                    <Am5Loader>
                        <ProgressionChart {id} athletes={$userParams.athletes} data={chartData} range={chartRange} {changedDates} />
                    </Am5Loader>
                    {#if adaptedFlash}
                        <div class="absolute inset-0 flex items-center justify-center pointer-events-none" transition:fade={{ duration: 250 }}>
                            <div class={cn('flex items-center gap-2 rounded-md bg-white/90 px-6 py-3 text-xl font-bold shadow-lg',
                                adaptedFlash === 'success' && 'text-green-800', adaptedFlash === 'warning' && 'text-amber-800', adaptedFlash === 'error' && 'text-red-700')}>
                                {#if adaptedFlash === 'error'}
                                    <i class="fa-solid fa-circle-xmark"></i>Couldn't Adapt Training Plan
                                {:else if adaptedFlash === 'warning'}
                                    <i class="fa-solid fa-triangle-exclamation"></i>Training Plan Adapted
                                {:else}
                                    <i class="fa-solid fa-circle-check"></i>Training Plan Adapted
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>
                <div class="mt-2" hidden={activeTab !== 'details'}>
                    {#if detailsReady}
                        <AdaptForecastDetails before={detailsBefore} after={detailsAfter} />
                    {/if}
                </div>
            </div>
        {/if}

        <Dialog.Footer>
            {#if modalState == 'confirmation'}
                <Button on:click={closeModal} variant='outline'>Cancel</Button>
                <Button on:click={_onConfirm}>Continue</Button>
            {:else}
                <div class="flex items-center gap-2 mr-auto sm:mr-2">
                    <Checkbox id="adapt-schedule-workouts" bind:checked={scheduleWorkouts} on:click={() => scheduleWorkoutsTouched = true} disabled={doRunTrainingForecast || !forecastResultsForOutput}/>
                    <Label class="mb-0 font-normal text-sm cursor-pointer" for="adapt-schedule-workouts">Schedule Workouts</Label>
                    <HelpTooltip message={SCHEDULE_WORKOUTS_HELP} label="About Schedule Workouts" iconClass="w-5 h-5 text-gray-500 -ml-1"/>
                </div>
                <Button disabled={doRunTrainingForecast || !forecastResultsForOutput} on:click={scheduleForecast}>Save Forecast</Button>
            {/if}
        </Dialog.Footer>
    </Dialog.Content>
</Dialog.Root>
